import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { IRootState } from "../../../../../reducer/CombineReducer";
import { OutputConfigSlice } from "../../redux";
import { BandModeType } from "../Types";
import { GetAgeBandFeatureFromClint } from "../Helper";
import { AGE_FEATURE } from "../Constant";
import { BandingAgeFormView } from "./BandingAgeForm.view";
import { AgeRangeType, AgeTypes } from "./Types";

interface BandingAgeFormProps {
  /**
   * Onchange for Band name
   */
  onChange?: (name: string) => void;
  /**
   * Banding name in textfield
   */
  bandingName: string;
}

/**
 * Responsible for Age banding popup logic
 * @returns Age banding popup logic
 */
export const BandingAgeForm: React.FC<BandingAgeFormProps> = (props: BandingAgeFormProps) => {
  const dispatch = useDispatch();
  const outputConfig = useSelector((state: IRootState) => state.outputConfig);
  const { definition, bandMode } = outputConfig.selectedBandingDetails;
  const [selectedFromOption, setSelectedFromOption] = useState(AgeTypes.DATE);
  const [selectedToOption, setSelectedToOption] = useState(AgeTypes.DATE);

  /**
   * Handler to update the age values
   * @param value updated date value
   */
  const onUpdateAge = (ageRange: AgeRangeType): AgeRangeType => {
    const updatedAgeRange = {
      dateRange: ageRange.data.dateRange,
      operator: ageRange.data.operator,
      value: ageRange.data.value,
      valueUnit: ageRange.data.valueUnit
    };

    return {
      data: updatedAgeRange,
      isValid: ageRange.isValid
    };
  };

  /**
   * To set the from entry
   * @param ageFeatureForFrom contains feature object
   */
  const setFromRange = (ageFeatureForFrom: any): void => {
    if (ageFeatureForFrom[0].featureType === AGE_FEATURE) {
      const rangeFrom = onUpdateAge({ data: ageFeatureForFrom[0], isValid: true });
      dispatch(OutputConfigSlice.actions.setSelectedFromType(AgeTypes.AGE));
      setSelectedFromOption(AgeTypes.AGE);
      dispatch(OutputConfigSlice.actions.setSelectedAgeValueFrom(rangeFrom));
    } else {
      dispatch(OutputConfigSlice.actions.setSelectedFromType(AgeTypes.DATE));
      setSelectedFromOption(AgeTypes.DATE);
      dispatch(OutputConfigSlice.actions.setSelectedAgeRangeFrom({
        value: ageFeatureForFrom[0].dateRange
      }));
    }
  };

  /**
   * To set the to entry
   * @param ageFeatureForTo contains feature object
   */
  const setToRange = (ageFeatureForTo: any): void => {
    if (ageFeatureForTo[0].featureType === AGE_FEATURE) {
      const rangeTo = onUpdateAge({ data: ageFeatureForTo[0], isValid: true });
      dispatch(OutputConfigSlice.actions.setSelectedToType(AgeTypes.AGE));
      setSelectedToOption(AgeTypes.AGE);
      dispatch(OutputConfigSlice.actions.setSelectedAgeValueTo(rangeTo));
    } else {
      dispatch(OutputConfigSlice.actions.setSelectedToType(AgeTypes.DATE));
      setSelectedToOption(AgeTypes.DATE);
      dispatch(OutputConfigSlice.actions.setSelectedAgeRangeTo({
        value: ageFeatureForTo[0].dateRange
      }));
    }
  };

  /**
   * To set the none in from entry
   */
  const setFromNoneType = (): void => {
    dispatch(OutputConfigSlice.actions.setSelectedFromType(AgeTypes.NONE));
    setSelectedFromOption(AgeTypes.NONE);
  };

  /**
   * To set the none in to entry
   */
  const setToNoneType = (): void => {
    dispatch(OutputConfigSlice.actions.setSelectedToType(AgeTypes.NONE));
    setSelectedToOption(AgeTypes.NONE);
  };

  React.useEffect(() => {
    if (bandMode === BandModeType.EDIT) {
      const { ageFeatureForFrom, ageFeatureForTo } = GetAgeBandFeatureFromClint(definition as string, false);
      if (ageFeatureForFrom) {
        setFromRange(ageFeatureForFrom);
      } else if (ageFeatureForFrom === undefined) {
        setFromNoneType();
      }
      if (ageFeatureForTo) {
        setToRange(ageFeatureForTo);
      } else if (ageFeatureForTo === undefined) {
        setToNoneType();
      }
    }
  }, [bandMode]);

  /**
   * Handler to update the date values
   * @param value updated date value
   */
  const onUpdate = (value): void => {
    const shouldUpdate = JSON.stringify(outputConfig.selectedAgeRangeFrom.value) === JSON.stringify(value);
    if (value && !shouldUpdate) {
      dispatch(OutputConfigSlice.actions.setSelectedAgeRangeFrom({
        value: value
      }));
    }
  };

  /**
   * Handler to update the date values
   * @param value updated date value
   */
  const onUpdateTo = (value): void => {
    const shouldUpdate = JSON.stringify(outputConfig.selectedAgeRangeTo.value) === JSON.stringify(value);
    if (value && !shouldUpdate) {
      dispatch(OutputConfigSlice.actions.setSelectedAgeRangeTo({
        value: value
      }));
    }
  };

  /**
   * Handler to update the date values
   * @param ageRange updated date value
   */
  const onUpdateAgeFromValue = (ageRange: AgeRangeType): void => {
    const fromValue = onUpdateAge(ageRange);
    dispatch(OutputConfigSlice.actions.setSelectedAgeValueFrom(fromValue));
  };

  /**
   * Handler to update the date values
   * @param ageRange updated date value
   */
  const onUpdateAgeToValue = (ageRange: AgeRangeType): void => {
    const toValue = onUpdateAge(ageRange);
    dispatch(OutputConfigSlice.actions.setSelectedAgeValueTo(toValue));
  };

  /**
   * To set the band name
   * @param val updated value
   */
  const handleNameChange = (e: React.SyntheticEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
    if (props.onChange)
      props.onChange(e.target["value"]);
  };

  /**
   * To set the selected from option
   * @param selected selected option
   */
  const onSelectRangeFrom = (selected: string): void => {
    setSelectedFromOption(selected as AgeTypes);
    dispatch(OutputConfigSlice.actions.setSelectedFromType(selected as AgeTypes));
  };

  /**
   * To set the selected to option
   * @param selected selected option
   */
  const onSelectRangeTo = (selected: string): void => {
    setSelectedToOption(selected as AgeTypes);
    dispatch(OutputConfigSlice.actions.setSelectedToType(selected as AgeTypes));
  };

  return (
    <BandingAgeFormView
      onUpdate={onUpdate}
      onUpdateTo={onUpdateTo}
      onNameChange={handleNameChange}
      onSelectRangeFrom={onSelectRangeFrom}
      onSelectRangeTo={onSelectRangeTo}
      onUpdateAgeFromValue={onUpdateAgeFromValue}
      onUpdateAgeToValue={onUpdateAgeToValue}
      selectRangeFromOption={selectedFromOption}
      selectRangeToOption={selectedToOption}
      bandingName={props.bandingName}
    />
  );
};
